// Right-pane camera selector (dark; mirrors the dashboard right-pane library).
//
// Renders one collapsible group per location with toggle cards inside. The
// scope budget bar lives at the bottom (`N / 12 on timeline · M/4 in grid`).
//
// All state mutation flows through `useScopeSelection` — this component is a
// rendered view of that hook, plus a search input that filters the list.

import { useMemo } from "react";

import { CAM_CAP, PLAY_CAP, type ScopeCamera } from "../hooks/useScopeSelection";

export interface ScopeLocation {
  /** Canonical location_id (passed back to handlers). */
  location_id: string;
  /** Operator-facing display name ("ZAS6"). */
  display_name: string;
  /** Optional sub-line ("Austin, TX"). */
  address?: string | null;
  /** Cameras in this location, in display order. */
  cameras: readonly ScopeCamera[];
  online: boolean;
  /** Initial expand-collapse state. */
  open: boolean;
}

export interface ScopeRailProps {
  locations: readonly ScopeLocation[];
  selected: ReadonlyMap<string, ScopeCamera>;
  gridSel: readonly string[];
  inGridView: boolean;
  searchQuery: string;
  capReached: boolean;
  onSearchChange(q: string): void;
  onToggleCamera(cam: ScopeCamera, on: boolean): void;
  onToggleLocation(cams: readonly ScopeCamera[], add: boolean): void;
  /** Toggle the open/closed state of a location group. */
  onToggleOpen(locationId: string): void;
  /** Whether to render the dismissible cap banner. */
  onDismissCap?(): void;
}

const ADD_SVG = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const CHECK_SVG = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function camThumbStyle(doorbotId: string): React.CSSProperties {
  let h = 0;
  for (const ch of doorbotId) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const a = h % 360;
  const b = (h >> 3) % 360;
  return {
    background: `linear-gradient(135deg,hsl(${a} 32% 26%),hsl(${b} 38% 38%))`,
  };
}

export function ScopeRail({
  locations,
  selected,
  gridSel,
  inGridView,
  searchQuery,
  capReached,
  onSearchChange,
  onToggleCamera,
  onToggleLocation,
  onToggleOpen,
  onDismissCap,
}: ScopeRailProps): JSX.Element {
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q === "") return locations;
    return locations
      .map((loc) => {
        const matchLoc =
          loc.display_name.toLowerCase().includes(q) ||
          (loc.address ?? "").toLowerCase().includes(q);
        const cams = matchLoc
          ? loc.cameras
          : loc.cameras.filter((c) => c.name.toLowerCase().includes(q));
        if (!matchLoc && cams.length === 0) return null;
        return { ...loc, open: true, cameras: cams };
      })
      .filter((x): x is ScopeLocation => x !== null);
  }, [locations, searchQuery]);

  const totalSelected = selected.size;
  const gridCount = gridSel.filter((id) => selected.has(id)).length;
  const atCap = totalSelected >= CAM_CAP;

  return (
    <aside className="rec-col rec-scope" aria-label="Cameras">
      <div className="rec-col-h">
        <h2>Cameras</h2>
        <span className="rec-count">{totalSelected} selected</span>
      </div>
      <div className="rec-pad">
        <div className="rec-search-input">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search location or camera…"
            aria-label="Search cameras"
          />
        </div>
        <div className="rec-cap-note">
          <svg width="14" height="14" viewBox="0 0 16 16" style={{ flex: "0 0 auto", marginTop: 1 }} aria-hidden="true">
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <rect x="7.2" y="6.6" width="1.6" height="5" rx=".8" fill="currentColor" />
            <circle cx="8" cy="4.4" r="1" fill="currentColor" />
          </svg>
          <span>
            Add up to <b>{CAM_CAP}</b> cameras to the timeline · play up to <b>{PLAY_CAP}</b> at once.
          </span>
        </div>
      </div>
      <div className="rec-scroll" id="rec-loc-list">
        {filtered.map((loc) => {
          const selHere = loc.cameras.filter((c) => selected.has(c.doorbot_id)).length;
          const allSelected = selHere === loc.cameras.length && loc.cameras.length > 0;
          const isOpen = loc.open;
          return (
            <div key={loc.location_id} className={`rec-loc${isOpen ? " open" : ""}`}>
              <div
                className="rec-loc-h"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest(".rec-loc-addall")) return;
                  onToggleOpen(loc.location_id);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggleOpen(loc.location_id);
                  }
                }}
                aria-expanded={isOpen}
              >
                <svg className="rec-chev" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                  <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div>
                  <div className="rec-loc-name">{loc.display_name}</div>
                  <div className="rec-loc-sub">
                    {loc.address ?? ""}
                    {loc.address ? " · " : ""}
                    {loc.cameras.length} cams
                    {selHere > 0 ? ` · ${selHere} added` : ""}
                  </div>
                </div>
                <div className="rec-loc-meta">
                  <button
                    type="button"
                    className="rec-loc-addall"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleLocation(loc.cameras, !allSelected);
                    }}
                    aria-label={`${allSelected ? "Remove" : "Add"} all cameras at ${loc.display_name}`}
                  >
                    {allSelected ? "Remove all" : "Add all"}
                  </button>
                  <span className={`rec-st${loc.online ? "" : " off"}`} aria-hidden="true" />
                </div>
              </div>
              {isOpen && (
                <div className="rec-cams">
                  {loc.cameras.map((cam) => {
                    const on = selected.has(cam.doorbot_id);
                    return (
                      <div
                        key={cam.doorbot_id}
                        className={`rec-cam${on ? " on" : ""}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => onToggleCamera(cam, !on)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onToggleCamera(cam, !on);
                          }
                        }}
                        aria-pressed={on}
                        aria-label={`${on ? "Remove" : "Add"} ${cam.name}`}
                      >
                        <span className="rec-cam-thumb" style={camThumbStyle(cam.doorbot_id)}>
                          <span className={`rec-st${cam.online ? "" : " off"}`} aria-hidden="true" />
                        </span>
                        <span className="rec-cam-meta">
                          <span className="rec-cam-name">{cam.name}</span>
                          <span className="rec-cam-loc">{loc.display_name}</span>
                        </span>
                        <button
                          type="button"
                          className="rec-cam-toggle"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleCamera(cam, !on);
                          }}
                          aria-label={`${on ? "Remove" : "Add"} ${cam.name}`}
                          aria-pressed={on}
                        >
                          {on ? CHECK_SVG : ADD_SVG}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ padding: 24, color: "var(--c-sidebar-fg-2)", fontSize: 12 }}>
            No locations match. Clear the search to see the full list.
          </div>
        )}
      </div>
      <div
        className={`rec-scope-budget${atCap ? " warn" : ""}`}
        role="status"
        aria-live="polite"
      >
        {totalSelected === 0 ? (
          "No cameras added — pick from the list above."
        ) : (
          <>
            <b>
              {totalSelected} / {CAM_CAP}
            </b>{" "}
            on timeline
            {atCap ? " · cap reached" : ` · ${CAM_CAP - totalSelected} free`}
            {inGridView && (
              <>
                {" "}
                · <b>
                  {gridCount}/{PLAY_CAP}
                </b>{" "}
                in grid
              </>
            )}
          </>
        )}
        {capReached && onDismissCap && (
          <>
            {" "}
            <button
              type="button"
              onClick={onDismissCap}
              style={{
                background: "transparent",
                border: 0,
                color: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
                marginLeft: 6,
              }}
              aria-label="Dismiss cap notice"
            >
              dismiss
            </button>
          </>
        )}
      </div>
    </aside>
  );
}
